import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import { notification } from "../../../utils/utility";
import { getAdminBusinessId } from "../../../utils/authHelper";
import { confirmStripeCheckout } from "../../../utils/confirmStripeCheckout";
import { isTrialEligible, trialPaymentCopy } from "../../../utils/trialAccess";
import { fetchUserFeatures } from "../../../actions/featureAccessActions";

/* ─── Design tokens (kept in sync with card-info-modal.js) ──────────────── */
const C = {
  orange:      "#FE9920",
  orangeLight: "#FFF7ED",
  dark:        "#111827",
  mid:         "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
  border:      "#D1D5DB",
  borderFocus: "#FE9920",
  error:       "#EF4444",
  errorBg:     "#FEF2F2",
  errorBorder: "#FECACA",
  errorDark:   "#991B1B",
  green:       "#10B981",
  greenBg:     "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenDark:   "#065F46",
};

/* ─── Stripe CardElement style ───────────────────────────────────────────── */
const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: "15px",
      color: C.dark,
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSmoothing: "antialiased",
      "::placeholder": { color: C.faint },
    },
    invalid: { color: C.error, iconColor: C.error },
  },
};

/* ─── Shared field style helper ─────────────────────────────────────────── */
const fieldStyle = (hasError = false) => ({
  width: "100%",
  border: `1px solid ${hasError ? C.error : C.border}`,
  borderRadius: "8px",
  padding: "10px 14px",
  fontSize: "14px",
  color: C.dark,
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
  transition: "border-color 0.15s, box-shadow 0.15s",
  fontFamily: "inherit",
});

/* ─── Inline CSS injected once ───────────────────────────────────────────── */
const FORM_STYLES = `
  @keyframes sco-spin { to { transform: rotate(360deg); } }

  .sco-input:focus {
    border-color: #FE9920 !important;
    box-shadow: 0 0 0 3px rgba(254,153,32,0.18) !important;
  }
  .sco-stripe-wrapper:focus-within {
    border-color: #FE9920 !important;
    box-shadow: 0 0 0 3px rgba(254,153,32,0.18) !important;
  }
  .sco-coupon-input:focus {
    border-color: #FE9920 !important;
    box-shadow: 0 0 0 3px rgba(254,153,32,0.18) !important;
    outline: none;
  }
  .sco-btn-submit:focus-visible,
  .sco-btn-coupon:focus-visible,
  .sco-btn-promo-toggle:focus-visible,
  .sco-btn-remove:focus-visible {
    outline: 2px solid #FE9920;
    outline-offset: 2px;
  }
  .sco-btn-submit:not(:disabled):hover {
    background: #e8880e !important;
  }
`;

/* ─── CardForm ───────────────────────────────────────────────────────────── */
const CardForm = ({
  onClose,
  selectedPlan,
  planPeriodType,
  getUserAndPlans,
  /* price / coupon values from parent */
  couponCode,
  price,
  discount,
  total,
  /* coupon UI state from parent (CardInfoModal) */
  couponInput,
  setCouponInput,
  appliedCoupon,
  couponLoading,
  couponError,
  couponOpen,
  setCouponOpen,
  onApplyCoupon,
  onRemoveCoupon,
}) => {
  const stripe   = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const userPlan = useSelector((state) => state.subscription?.userPlan);
  const trialEligible = isTrialEligible(userPlan);
  const trialCopy = trialPaymentCopy(trialEligible);

  /* Local form state */
  const [cardholderName, setCardholderName] = useState("");
  const [nameError,      setNameError]      = useState("");
  const [cardError,      setCardError]      = useState(null);
  const [submitError,    setSubmitError]    = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [overlayActive,  setOverlayActive]  = useState(false);

  /* ── Submit handler ─────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setNameError("");

    /* Validate cardholder name */
    if (!cardholderName.trim()) {
      setNameError("Please enter the cardholder name.");
      return;
    }
    if (!stripe || !elements) return;
    if (loading) return; /* prevent duplicate clicks */

    setLoading(true);

    /* 1 ── Create payment method via Stripe.js */
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: { name: cardholderName.trim() },
    });

    if (pmError) {
      setCardError(pmError.message);
      setLoading(false);
      return;
    }
    setCardError(null);

    /* 2 ── Create subscription on backend */
    let createRes;
    try {
      const payload = {
        payment_method_id: paymentMethod.id,
        plan_id:           selectedPlan.id,
        plan_type:         planPeriodType,
        business_id:       getAdminBusinessId(),
      };
      if (couponCode) payload.coupon_code = couponCode;

      createRes = await axiosInstance.post("/admin/subscription/create", payload);
    } catch (apiErr) {
      setSubmitError(
        apiErr?.response?.data?.message || "Something went wrong. Please try again."
      );
      setLoading(false);
      return;
    }

    if (!createRes?.data?.success) {
      setSubmitError(
        createRes?.data?.message || "Could not create subscription. Please try again."
      );
      setLoading(false);
      return;
    }

    const { client_secret: clientSecret, setup_client_secret: setupClientSecret, subscription_id: subscriptionId } = createRes.data;

    const { error: confirmError } = await confirmStripeCheckout({
      stripe,
      elements,
      clientSecret,
      setupClientSecret,
      cardholderName: cardholderName.trim(),
    });

    if (confirmError) {
      setSubmitError(confirmError.message);
      setLoading(false);
      return;
    }

    await confirmSubscription(subscriptionId);
  };

  /* ── Notify backend and provision the subscription ─────────────────── */
  const confirmSubscription = async (subscriptionId) => {
    try {
      await axiosInstance.post("/admin/subscription/confirm", {
        subscription_id: subscriptionId,
        business_id:     getAdminBusinessId(),
      });
      dispatch(fetchUserFeatures());
    } catch (err) {
      /* Non-blocking — payment succeeded, provisioning may retry via webhook */
      console.error("Subscription confirm error", err);
    }
    notification.success(trialEligible ? "Your 14-day trial has started" : "Successfully subscribed to plan");
    setOverlayActive(true);
    setTimeout(() => {
      setOverlayActive(false);
      getUserAndPlans();
      setLoading(false);
      onClose();
    }, 2000);
  };

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <>
      <style>{FORM_STYLES}</style>

      <Loader
        isShowLoader={overlayActive}
        loadingText={
          trialEligible
            ? "Starting your 14-day trial…"
            : "Setting up your subscription…"
        }
      />

      <form onSubmit={handleSubmit} noValidate>

        {/* ── Cardholder Name ───────────────────────────────── */}
        <div style={{ marginBottom: "18px" }}>
          <label
            htmlFor="sco-name"
            style={{ display: "block", fontSize: "13px", fontWeight: 500, color: C.mid, marginBottom: "6px" }}
          >
            Cardholder name
          </label>
          <input
            id="sco-name"
            className="sco-input"
            type="text"
            value={cardholderName}
            onChange={(e) => { setCardholderName(e.target.value); setNameError(""); }}
            placeholder="Full name on card"
            autoComplete="cc-name"
            aria-describedby={nameError ? "sco-name-err" : undefined}
            aria-invalid={!!nameError}
            style={fieldStyle(!!nameError)}
          />
          {nameError && (
            <p id="sco-name-err" role="alert" style={{ margin: "5px 0 0", fontSize: "12px", color: C.error }}>
              {nameError}
            </p>
          )}
        </div>

        {/* ── Card Details (Stripe Element) ─────────────────── */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{ display: "block", fontSize: "13px", fontWeight: 500, color: C.mid, marginBottom: "6px" }}
          >
            Card details
          </label>
          <div
            className="sco-stripe-wrapper"
            style={{
              border: `1px solid ${cardError ? C.error : C.border}`,
              borderRadius: "8px",
              padding: "11px 14px",
              background: "#fff",
              transition: "border-color 0.15s, box-shadow 0.15s",
            }}
          >
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => {
                setCardError(e.error ? e.error.message : null);
                setSubmitError(null);
              }}
            />
          </div>
          {cardError && (
            <p role="alert" style={{ margin: "5px 0 0", fontSize: "12px", color: C.error }}>
              {cardError}
            </p>
          )}
        </div>

        {/* ── Promo Code (progressive disclosure) ──────────── */}
        <div style={{ marginBottom: "22px" }}>
          {appliedCoupon ? (
            /* Applied state */
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: C.greenBg,
              border: `1px solid ${C.greenBorder}`,
              borderRadius: "8px",
              padding: "10px 14px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {/* Checkmark icon */}
                <svg width="15" height="15" viewBox="0 0 20 20" fill={C.green} aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span style={{ fontSize: "13px", fontWeight: 600, color: C.greenDark }}>
                  &ldquo;{appliedCoupon.code}&rdquo; —{" "}
                  {appliedCoupon.type === "P"
                    ? `${appliedCoupon.value}% off`
                    : `$${parseFloat(appliedCoupon.value).toFixed(2)} off`}
                </span>
              </div>
              <button
                type="button"
                className="sco-btn-remove"
                onClick={onRemoveCoupon}
                aria-label="Remove coupon"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: C.muted,
                  padding: "2px 4px",
                  textDecoration: "underline",
                  borderRadius: "4px",
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            /* Closed / open toggle */
            <>
              <button
                type="button"
                className="sco-btn-promo-toggle"
                onClick={() => setCouponOpen(!couponOpen)}
                aria-expanded={couponOpen}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: C.orange,
                  fontWeight: 500,
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  borderRadius: "4px",
                }}
              >
                {/* Chevron */}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ transform: couponOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Have a promo code?
              </button>

              {couponOpen && (
                <div style={{ marginTop: "10px" }}>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      className="sco-coupon-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          onApplyCoupon();
                        }
                      }}
                      placeholder="PROMO CODE"
                      aria-label="Promo code"
                      aria-describedby={couponError ? "sco-coupon-err" : undefined}
                      aria-invalid={!!couponError}
                      style={{
                        flex: 1,
                        border: `1px solid ${couponError ? C.error : C.border}`,
                        borderRadius: "8px",
                        padding: "9px 12px",
                        fontSize: "13px",
                        fontFamily: "'Courier New', Courier, monospace",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        color: C.dark,
                        outline: "none",
                        background: "#fff",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                      }}
                    />
                    <button
                      type="button"
                      className="sco-btn-coupon"
                      onClick={onApplyCoupon}
                      disabled={couponLoading || !couponInput.trim()}
                      style={{
                        background: couponLoading || !couponInput.trim() ? "#F3F4F6" : C.dark,
                        color:      couponLoading || !couponInput.trim() ? C.faint : "#fff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "9px 18px",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: couponLoading || !couponInput.trim() ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        minWidth: "68px",
                        minHeight: "44px",
                        transition: "background 0.15s",
                      }}
                    >
                      {couponLoading ? "…" : "Apply"}
                    </button>
                  </div>
                  {couponError && (
                    <p id="sco-coupon-err" role="alert" style={{ margin: "6px 0 0", fontSize: "12px", color: C.error }}>
                      {couponError}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Submission Error ──────────────────────────────── */}
        {submitError && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              background: C.errorBg,
              border: `1px solid ${C.errorBorder}`,
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
              fontSize: "13px",
              color: C.errorDark,
              lineHeight: 1.5,
            }}
          >
            {/* Warning icon */}
            <svg width="15" height="15" viewBox="0 0 20 20" fill={C.error} style={{ flexShrink: 0, marginTop: "1px" }} aria-hidden="true">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {submitError}
          </div>
        )}

        {/* ── Submit Button ─────────────────────────────────── */}
        <button
          type="submit"
          className="sco-btn-submit"
          disabled={loading || !stripe}
          aria-busy={loading}
          style={{
            width: "100%",
            background: loading || !stripe ? "#FED7AA" : C.orange,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            padding: "14px 20px",
            fontSize: "15px",
            fontWeight: 700,
            cursor: loading || !stripe ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            letterSpacing: "0.2px",
            transition: "background 0.15s",
            marginBottom: "14px",
            minHeight: "52px",
          }}
        >
          {loading ? (
            <>
              <span
                aria-hidden="true"
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid rgba(255,255,255,0.35)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "sco-spin 0.7s linear infinite",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              Processing…
            </>
          ) : (
            trialCopy.submitLabel(total)
          )}
        </button>

        {/* ── Legal + Security note ─────────────────────────── */}
        <p style={{ textAlign: "center", fontSize: "11px", color: C.faint, margin: 0, lineHeight: 1.7 }}>
          By subscribing you agree to our{" "}
          <a href="/terms"   target="_blank" rel="noopener noreferrer" style={{ color: C.muted, textDecoration: "underline" }}>Terms of Service</a>{" "}
          and{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.muted, textDecoration: "underline" }}>Privacy Policy</a>.
        </p>
        <p style={{
          textAlign: "center",
          fontSize: "11px",
          color: C.faint,
          margin: "8px 0 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "5px",
          lineHeight: 1,
        }}>
          {/* Lock icon */}
          <svg width="11" height="11" viewBox="0 0 20 20" fill={C.faint} aria-hidden="true">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Secured by Stripe
        </p>

      </form>
    </>
  );
};

export default CardForm;
